import { List, Map } from 'immutable'
import { Action, any, async, AsyncState, browserRouter, Fun, loadedAsyncState, loadingAsyncState, none, nothing, Option, Pair, some, unloadedAsyncState, Widget } from 'widgets-for-react'
import { BlocksRenderer } from '../cms/blocks/block_to_widget.gen'
import { link, linkWrapper } from '../cms/blocks/utils.gen'
import { Page } from '../cms/page/types.gen'
import { CustomRouteParams } from '../cms/router/type.gen'
import { AppState, PageTemplateState } from '../cms/state/types.gen'
import { PageStateZero } from '../cms/state/utils.gen'
import { EntryClient } from '../cms/widget.gen'
import { ContentType, defaultPage, emptyAppState, HeaderAccountMenu, HeaderMenuBlockItem, HeaderMenuItem, HeaderMenuItems, HeaderMenuLinkListItem, HeaderState, OptionsState, PublicSiteState } from '../public_site.main.mod'
import { SuccessFactors } from '../successfactors/api'
import { WordPressBlockImagesLoader } from '../wordpress/rendering/image_utils.mod'
import { CustomBlock } from '../wordpress/types_and_parser/custom_blocks.gen'
import { Breadcrumb } from '../wordpress/types_and_parser/extra_types.mod'
import { Block } from '../wordpress/types_and_parser/standard_blocks.gen'
import { ParsedNode } from '../wordpress/types_and_parser/types.mod'
import { WordPress } from '../wordpress/wordpress_api'
import { CustomBlock_To_Widget, parseVideo, TransformStandardBlock, WordPressBlock_To_WidgetBlock } from '../wordpress/wordpress_to_cms.mod'
import { CustomBlockData } from './blocks/custom_blocks/types.mod'
import { pageDisplay } from './blocks/renderer.mod'
import { proxyPageLinkWrapper } from './blocks/utils.mod'
import { CustomPages, PublicSiteCustomRoutes } from './custom_pages/types.mod'
import { cookiebar } from './templates/cookiebar.mod'
import { PageHeaderFooterProps } from './templates/page.mod'
import { ExtraImages } from './utils/extra_images.mod'
import { moment } from './utils/moment'

export type PublicSite_CustomPageData = {
  posts: Map<number, AsyncState<WordPress.PostTypeResponse>>
  post: WordPress.PostTypeResponse
  latest_articles: AsyncState<WordPress.PostTypeResponse[]>
  vacancies?: AsyncState<SuccessFactors.Vacancy[]>
}

export function asyncPost<PT>(id: number): AsyncState<WordPress.PostTypeResponse> {
  return loadingAsyncState<WordPress.PostTypeResponse>(_ =>
    WordPress.api()
      .anypost(id)
      .then(response =>
        response.data ? { kind: 'result', status: 200, value: response.data } : { kind: 'failed', status: 404 }
      )
  )
}

export function asyncPosts(args?: WordPress.PostRequestArguments): AsyncState<WordPress.PostTypeResponse[]> {
  return loadingAsyncState<WordPress.PostTypeResponse[]>(_ =>
    WordPress.api<WordPress.PostTypeResponse>()
      .select('post')
      .where(args)
      .getMultiple()
      .then(response =>
        response.data
          ? { kind: 'result', status: 200, value: response.data }
          : {
            kind: 'failed',
            status: 404
          }
      )
  )
}

function loadExtraPostData(post: WordPress.PostTypeResponse): PublicSite_CustomPageData {
  let posts = Map<number, AsyncState<WordPress.PostTypeResponse>>()
  let latest_articles = unloadedAsyncState<WordPress.PostTypeResponse[]>()

  post.acf.buildingblocks &&
    post.acf.buildingblocks.forEach(block => {
      switch (block.acf_fc_layout) {
        default:
          break
      }
    })

  return {
    posts,
    post,
    latest_articles
  }
}

export const InitPagesStates: (
  blocks_renderer: BlocksRenderer<CustomBlockData, ExtraImages>
) => PageTemplateState<CustomBlockData, CustomBlock, Block, ExtraImages, PublicSite_CustomPageData> = b => ({
  page: PageStateZero<CustomBlockData, CustomBlock, Block, ExtraImages, PublicSite_CustomPageData>(
    () => unloadedAsyncState(),
    defaultPage,
    pageDisplay(b)
  ).page
})

export const get_page = (slug: string): AsyncState<Page<CustomBlock, Block, PublicSite_CustomPageData>> =>
  loadingAsyncState(_ => {
    return WordPress.api()
      .current(slug)
      .then(response => (
        response.data && response.data.acf ? {
          kind: 'result',
          status: 200,
          value: {
            blocks: response.data.acf.buildingblocks
              ? response.data.acf.buildingblocks.map(b => TransformStandardBlock(b))
              : [],
            extra_data: loadExtraPostData(response.data)
          },
        } : {
            kind: 'failed',
            status: 404
          }
      ))
      .catch(e => {
        console.error(e)
        return e
      })
  })

export function get_options(id: string): AsyncState<WordPress.OptionsResponse> {
  return loadingAsyncState<WordPress.OptionsResponse>(_ =>
    WordPress.api()
      .options(id)
      .then(response => {
        return response.data
          ? { kind: 'result', status: 200, value: response.data }
          : {
            kind: 'failed',
            status: 404
          }
      })
  )
}

const getPost = (id: number) =>
  WordPress.api()
    .anypost(id)
    .then(response => {
      console.log('response', response)
      return response.data
        ? ({ kind: 'result', status: 200, value: response.data } as any)
        : {
          kind: 'failed',
          status: 404
        }
    })
    .catch(e => {
      console.log(e)
      return { kind: 'failed', status: 500 } as any
    })

const optionsToAccountMenu = (
  options: WordPress.OptionsResponse,
  route: CustomRouteParams<PublicSiteState, PublicSiteCustomRoutes>
): HeaderAccountMenu => ({
  kind: 'account',
  open: false,
  items:
    options && options.acf && options.acf.sub_items
      ? options.acf.sub_items.map(i => ({
          link:
            proxyPageLinkWrapper(route)({ title: i.link.title, url: i.link.url })
        }))
      : []
})

const optionsToMenu = (
  options: WordPress.OptionsResponse,
  route: CustomRouteParams<PublicSiteState, PublicSiteCustomRoutes>
): HeaderMenuItems =>
  Map(
    options.acf.menu
      .map<HeaderMenuItem>(item =>
        item.submenu === 'gridblocks'
          ? {
              kind: 'blocks',
              open: false,
              title: item.title,
              blocks: List<HeaderMenuBlockItem>(
                item.gridblocks.map<HeaderMenuBlockItem>(b => ({
                  title: b.title,
                  subtitle: b.subtitle,
                  text: b.text,
                  link: proxyPageLinkWrapper(route)({
                    title: b.link ? b.link.title : undefined,
                    url: b.link ? b.link.url : undefined
                  })
                }))
              )
            }
          : item.submenu === 'linklist'
          ? {
              kind: 'linkList',
              open: false,
              title: item.title,
              list: List<HeaderMenuLinkListItem>()
                .push({
                  text: item.linklist_1_intro,
                  title: item.title,
                  links: List(
                    item.linklist_1.map(l =>
                      proxyPageLinkWrapper(route)({
                        title: l.link ? l.link.title : undefined,
                        url: l.link ? l.link.url : undefined
                      })
                    )
                  )
                })
                .push({
                  text: item.linklist_2_intro,
                  title: item.title,
                  links: List(
                    item.linklist_2.map(l =>
                      proxyPageLinkWrapper(route)({
                        title: l.link ? l.link.title : undefined,
                        url: l.link ? l.link.url : undefined
                      })
                    )
                  )
                })
            }
          : {
              ...proxyPageLinkWrapper(route)({
                title: item.link.title,
                url: item.link.url
              }),
              kind: 'link'
            }
      )
      .map((item, index) => [index, item])
  )

export const pageToHeaderFooterProps: Fun<Page<CustomBlock, Block, PublicSite_CustomPageData>, PageHeaderFooterProps<ExtraImages>> = page => {
  let post = page.extra_data.post

  let mediaProps = {
    kind_media: post.acf.kind_media ? post.acf.kind_media : 'none',
    image_url: post.acf.image ? post.acf.image.sizes["580w"] : undefined,
    image_alt_text: post.acf.kind_media === 'image' && post.acf.image ? post.acf.image_alt : undefined,
    video: post.acf.kind_media !== 'video' ? undefined : {
      ...parseVideo(post.acf.video),
      height: 200,
      width: 400,
      useCookiebar: false
    },
    title: post.title.rendered,
    subtitle: post.acf.hero_subtitle,
    intro: post.acf.hero_intro,
  }

  return ({
    header: post.type == "page" && post.is_front
      ? {
        kind: 'homepage',
        title: post.title.rendered
      } : post.type == "news_article" ? {
        kind: 'news',
        created: moment(post.date_gmt),
        author: {
          job: post.acf.author_job,
          name: post.acf.author_name,
          image_alt: post.acf.author_image ? post.acf.author_image.alt : '',
          image_src: post.acf.author_image ? post.acf.author_image.sizes.super_grid : ''
        },
        ...mediaProps,
      } : {
          kind: 'page',
          ...mediaProps
        },
    footer: post.type == "news_article" ? {
      kind: 'news',
      author: {
        job: post.acf.author_job,
        name: post.acf.author_name,
        image_alt: post.acf.author_image ? post.acf.author_image.alt : '',
        image_src: post.acf.author_image ? post.acf.author_image.sizes.super_grid : ''
      }
    } : {
        kind: 'empty'
      }
  })
}

export const closeMenuItems: Fun<HeaderMenuItems, HeaderMenuItems> = items =>
  items.map(i => (i.kind === 'blocks' || i.kind === 'linkList' ? { ...i, open: false } : i)).toMap()

export const closeAccountMenu: Action<HeaderAccountMenu> = items => ({...items, open: false})

export const application = (
  data: { rootpath: string; rootpath_to_ignore: string; login_address?: string },
  header: (_: HeaderState, o: OptionsState) => Widget<Action<HeaderState>>,
  footer: (
    _: AppState<PublicSiteState, PublicSiteCustomRoutes>
  ) => Widget<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>,
  errorPage: Widget<never>,
  loadingPage: Widget<never>,
  page: Fun<Pair<List<Breadcrumb>, Option<ParsedNode>>, Fun<PageHeaderFooterProps<ExtraImages>, ContentType>>,
  custom_pages: CustomPages
) => {
  let rootpath = data.rootpath ? data.rootpath : ''
  let rootpath_to_ignore = data.rootpath_to_ignore ? data.rootpath_to_ignore : ''

  return (s0: AppState<PublicSiteState, PublicSiteCustomRoutes>) =>
    browserRouter<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>()(
      s0.route.kind === 'ignore'
        ? nothing()
        : any<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>()([
          // Load WP options async.
          async<WordPress.OptionsResponse>(undefined, { key: 'async-options' })(s0.state.options)
            .map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(a => s => {
              let s1 = a(s.state.options)

              return s1.kind === 'loaded'
                ? {
                  ...s0,
                  state: {
                    ...s0.state,
                    options: s1
                  }
                }
                : s0
            })
            .map(a => s => {
              let s1 = a(s)

              if (s1.state.options.kind !== 'loaded') {
                return s1
              }

              return {
                ...s1,
                state: {
                  ...s1.state,
                  header: {
                    ...s1.state.header,
                    menu: loadedAsyncState({
                      account: optionsToAccountMenu(s1.state.options.value, s1.route),
                      headerMenuItems: optionsToMenu(s1.state.options.value, s1.route)
                    })
                  }
                }
              }
            }),

          // Load latest articles for current page.
          s0.state.page.page.kind === 'loaded'
            ? async<WordPress.PostTypeResponse[]>(undefined, { key: 'async-articles' })(s0.state.page.page.value.extra_data.latest_articles).map<
              Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
            >(a => s => {
              if (s.state.page.page.kind !== 'loaded') {
                return s
              }

              return {
                ...s,
                state: {
                  ...s.state,
                  page: {
                    ...s.state.page,
                    page: {
                      ...s.state.page.page,
                      value: {
                        ...s.state.page.page.value,
                        extra_data: {
                          ...s.state.page.page.value.extra_data,
                          latest_articles: a(s.state.page.page.value.extra_data.latest_articles)
                        }
                      }
                    }
                  }
                }
              }
            })
            : nothing(),

          // Load extra pots for current page.
          any<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>({ key: 'any-extra-posts' })(
            s0.state.page.page.kind === 'loaded'
              ? s0.state.page.page.value.extra_data.posts
                .map((p, key) =>
                  async<WordPress.PostTypeResponse>(undefined, { key: `async-extra-posts-${key}` })(p).map<
                    Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
                  >(a => s => {
                    if (s.state.page.page.kind !== 'loaded') {
                      return s
                    }
                    let a1 = a(s.state.page.page.value.extra_data.posts.get(key))
                    return {
                      ...s,
                      state: {
                        ...s.state,
                        page: {
                          ...s.state.page,
                          page: {
                            ...s.state.page.page,
                            value: {
                              ...s.state.page.page.value,
                              extra_data: {
                                ...s.state.page.page.value.extra_data,
                                posts: s.state.page.page.value.extra_data.posts.set(key, a1)
                              }
                            }
                          }
                        }
                      }
                    }
                  })
                )
                .valueSeq()
                .toArray()
              : [nothing()]
          ),

          header(s0.state.header, s0.state.options).map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(
            a => s1 => ({
              ...s1,
              state: { ...s1.state, header: a(s1.state.header) }
            })
          ),

          EntryClient<
            CustomBlockData,
            CustomBlock,
            Block,
            PublicSiteState,
            keyof CustomPages,
            PublicSiteCustomRoutes,
            ExtraImages,
            PublicSite_CustomPageData
          >({
            initState: emptyAppState(page),
            custom_pages,
            StandardBlockImagesLoader: WordPressBlockImagesLoader,
            StandardBlock_To_Widget: WordPressBlock_To_WidgetBlock,
            errorPage,
            loadingPage,
            rootpath,
            rootpath_to_ignore,
            CustomBlock_To_Widget
          })({
            ...s0,
            state: {
              ...s0.state,
              page: {
                ...s0.state.page,
                display: page({
                  fst:
                    s0.state.page.page.kind === 'loaded'
                      ? List(s0.state.page.page.value.extra_data.post.breadcrumbs)
                      : List(),
                  snd: s0.state.page.page.kind === 'loaded' ? some(s0.state.page.page.value.extra_data.post) : none()
                })(
                  s0.state.page.page.kind === 'loaded'
                    ? pageToHeaderFooterProps(s0.state.page.page.value)
                    : {
                      header: { kind: 'empty' },
                      footer: { kind: 'empty' }
                    }
                )
              }
            }
          }),

          footer(s0).map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(a => s1 => a(s1))
        ])
    ).map<AppState<PublicSiteState, PublicSiteCustomRoutes>>(a => {
      let s1 = a(s0)

      if (
        (s0.route.kind !== 'proxy_page' && s0.route.kind !== 'initial' && s1.route.kind === 'proxy_page') ||
        (s0.route.kind === 'proxy_page' && s1.route.kind !== 'proxy_page')
      ) {
        window.location.reload()
      }

      if (
        s1.state.page.page.kind === 'loaded' &&
        s1.state.page.page.value.extra_data.post.yoast &&
        s1.state.page.page.value.extra_data.post.yoast.title
      ) {
        document.title = s1.state.page.page.value.extra_data.post.yoast.title
      }

      if (
        s1.route.kind === '404' ||
        s1.route.kind === 'ignore' ||
        s0.route.kind === '404' ||
        s0.route.kind === 'ignore' ||
        (s0.route.kind === 'page' && s1.route.kind === 'page' && s0.route.slug !== s1.route.slug) ||
        (s0.state.page.page.kind !== 'loaded' && s1.state.page.page.kind === 'loaded')
      ) {
        if (s1.state.header.menu.kind === 'loaded') {
          s1 = {
            ...s1,
            state: {
              ...s1.state,
              header: {
                ...s1.state.header,
                menu: {
                  ...s1.state.header.menu,
                  value: {
                    headerMenuItems: closeMenuItems(s1.state.header.menu.value.headerMenuItems),
                    account: closeAccountMenu(s1.state.header.menu.value.account)
                  }
                },
                open: false
              }
            }
          }
        }
        window.scrollTo(0, 0)
      }

      return s1
    })
}
